import React, { useState, useRef, useEffect } from "react"

const isSafari = () => {
  const chromeInAgent = navigator.userAgent.indexOf("Chrome") > -1
  const safariInAgent = navigator.userAgent.indexOf("Safari") > -1
  return safariInAgent && !chromeInAgent
}

const registerOpenDropdownHandlers = ({
  optionsLength,
  activeIndex,
  setActiveIndex,
  select,
  namespace,
  options,
}) => {
  const keyDownCallback = e => {
    e.preventDefault()
    console.log(e)
    switch (e.key) {
      case "Up":
      case "ArrowUp":
        e.preventDefault()
        setActiveIndex(activeIndex <= 0 ? optionsLength - 1 : activeIndex - 1)
        return
      case "Down":
      case "ArrowDown":
        e.preventDefault()
        setActiveIndex(activeIndex + 1 === optionsLength ? 0 : activeIndex + 1)
        return
      case "Enter":
        e.preventDefault()
        options && select(options[activeIndex].value)
      case " ": // Space
        e.preventDefault()
        options && select(options[activeIndex].value)
        return
      case "Esc":
      case "Escape":
        e.preventDefault()
        select(false)
        return
      case "PageUp":
      case "Home":
        e.preventDefault()
        setActiveIndex(0)
        return
      case "PageDown":
      case "End":
        e.preventDefault()
        options && setActiveIndex(options.length - 1)
        return
    }
  }
  const onClick = e => {
    if (
      !e
        .composedPath()
        .find(
          e => e.dataset && e.dataset.namespace === namespace + "-dropdown-root"
        )
    ) {
      // Did not found in path, closing
      e.preventDefault()
      select(false)
    }
  }

  document.addEventListener("keydown", keyDownCallback)
  document.addEventListener("click", onClick)
  return () => {
    document.removeEventListener("keydown", keyDownCallback)
    document.removeEventListener("click", onClick)
  }
}

const registerClosedDropdownHandlers = ({ setIsDropdownOpen }) => {
  const keyDownCallback = e => {
    switch (e.key) {
      case "Up":
      case "ArrowUp":
      case "Down":
      case "ArrowDown":
      case " ": // Space
      case "Enter":
        e.preventDefault()
        setIsDropdownOpen(true)
    }
  }
  document.addEventListener("keydown", keyDownCallback)
  return () => {
    document.removeEventListener("keydown", keyDownCallback)
  }
}

const useAccessibleDropdown = ({ options, value, onChange, namespace }) => {
  const [isDropdownOpen, setIsDropdownOpenInternal] = useState(false)
  const listRef = useRef()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isFocus, setIsFocus] = useState(false)
  const select = value => {
    if (value) {
      onChange && onChange(value)
    }
    setIsDropdownOpen(false)
  }

  const setIsDropdownOpen = v => {
    if (v && options) {
      const selected = options.findIndex(o => o.value === value)
      setActiveIndex(selected < 0 ? 0 : selected)
      if (listRef.current && isSafari()) {
        requestAnimationFrame(() => {
          listRef.current.focus()
        })
      }
    } else {
      if (listRef.current && isSafari()) {
        requestAnimationFrame(() => {
          listRef.current.previousSibling.focus()
        })
      }
    }
    setIsDropdownOpenInternal(v)
  }

  useEffect(() => {
    if (isDropdownOpen) {
      return registerOpenDropdownHandlers({
        activeIndex,
        setActiveIndex,
        optionsLength: options && options.length,
        select,
        namespace,
        options,
      })
    } else {
      if (isFocus) {
        return registerClosedDropdownHandlers({
          setIsDropdownOpen,
        })
      }
    }
  }, [isDropdownOpen, activeIndex, isFocus, namespace])

  return {
    isDropdownOpen,
    setIsDropdownOpen,
    activeIndex,
    setActiveIndex,
    select,
    isFocus,
    setIsFocus,
    listRef,
  }
}

const Dropdown = ({
  options,
  value,
  namespace = "brand_select_namespace",
  onChange,
  label,
  placeholder,
  className,
  onBlur,
  onFocus,
  style = "form",
  icon,
  children,
  classNameDropdown,
}) => {
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    activeIndex,
    setActiveIndex,
    select,
    isFocus,
    setIsFocus,
    dropdownRootRef,
    listRef,
  } = useAccessibleDropdown({ options, value, onChange, namespace })
  const chosen = value && options.find(o => o.value === value)

  const doFocus = () => {
    if (onFocus) {
      onFocus(true)
    }
    setIsFocus(true)
  }
  const doBlur = () => {
    if (onBlur) {
      onBlur(true)
    }
    setIsFocus(false)
  }
  const doClick = () => {}

  return (
    <>
      <div className="relative" data-namespace={`${namespace}-dropdown-root`}>
        <button
          className={`brand-dropdown-trigger ${
            style === "naked"
              ? ``
              : `transition-all brand-form-item brand-form-input brand-form-select brand-form-padding brand-form-style focus:brand-form-focus`
          }${className ? ` ${className}` : ``}`}
          type="button"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          onFocus={() => doFocus()}
          onBlur={() => doBlur()}
          role="combobox"
          aria-autocomplete="none"
          aria-label={label && label}
          aria-haspopup="listbox"
          aria-controls={`${namespace}_dropdown`}
          aria-expanded={isDropdownOpen}
          aria-activedescendant={`${namespace}_element_${value}`}
        >
          {(options || placeholder) && (
            <span className="grid grid-cols-1">
              {options &&
                options.map((node, index) => {
                  return (
                    <span
                      className={`col-start-1 row-start-1 text-left ${
                        value === node.value ? `` : `opacity-0 invisible h-0`
                      }`}
                    >
                      {node.label}
                    </span>
                  )
                })}
              {placeholder && !value && (
                <span className={`col-start-1 row-start-1 text-left`}>
                  {placeholder}
                </span>
              )}
            </span>
          )}
          {style === "form" && (
            <span className="ml-auto material-icons">
              {isDropdownOpen ? "expand_less" : "expand_more"}
            </span>
          )}
          {icon ? <span className="material-icons">{icon}</span> : ""}
        </button>
        <ul
          className={`brand-dropdown${
            classNameDropdown
              ? ` ${classNameDropdown}`
              : ` brand-form-dropdown brand-form-item`
          }`}
          ref={listRef}
          role="listbox"
          id={`${namespace}_dropdown`}
          tabIndex={options ? "-1" : "0"}
        >
          {options ? (
            <>
              {options.map(({ label, value: optionValue, tag }, index) => {
                return (
                  <li
                    key={optionValue}
                    id={`${namespace}_element_${optionValue}`}
                    aria-selected={index === activeIndex}
                    role="option"
                    onMouseOver={() => setActiveIndex(index)}
                    className="brand-form-dropdown-option"
                  >
                    <label>
                      <input
                        type="radio"
                        name={`${namespace}_radio`}
                        value={optionValue}
                        className={chosen?.value === optionValue && "checked"}
                        checked={chosen?.value === optionValue}
                        onChange={() => select(optionValue)}
                      />
                      <span className="brand-form-dropdown-option-label">
                        {label}
                        {chosen?.value === optionValue ? (
                          <span className="ml-auto material-icons">check</span>
                        ) : (
                          ""
                        )}
                      </span>
                    </label>
                  </li>
                )
              })}
            </>
          ) : (
            <>{children}</>
          )}
        </ul>
      </div>
    </>
  )
}

export default Dropdown
